/**
 * Page Editor Component (New Version)
 * Main drag-and-drop page editor using Craft.js
 */
import { Editor as CraftEditor, Frame, Element } from "@craftjs/core";
import React, { useState, useEffect, useMemo } from "react";
import {
  Box,
  Button as MuiButton,
  IconButton,
  Typography,
  Paper,
  Chip,
  Tooltip,
  ToggleButton,
  ToggleButtonGroup,
} from "@mui/material";
import { ThemeProvider, createTheme } from "@mui/material/styles";
import {
  ChevronLeft as ChevronLeftIcon,
  ChevronRight as ChevronRightIcon,
  Close as CloseIcon,
  Settings as SettingsIcon,
  DesktopWindows as DesktopIcon,
  TabletMac as TabletIcon,
  PhoneIphone as MobileIcon,
} from "@mui/icons-material";
import { RootContainer } from "../blocks/Container";
import { PageData, PageListItem, ThemeSettings } from "../../types";
import { PreviewModal } from "../PreviewModal";
import { blockResolver } from "../../config/blockResolver";
import { defaultTheme } from "../../config/themeConstants";
import { getGoogleFontsUrl } from "../../config/apiConfig";
import { EditorTopBar } from "./EditorTopBar";
import { EditorShortcuts } from "./EditorShortcuts";
import { ThemeColorSync } from "./ThemeColorSync";
import { EditorLayersPanel } from "./panels/EditorLayersPanel";
import { EditorSettingsPanel } from "./panels/EditorSettingsPanel";
import { EditorToolbox } from "./panels/EditorToolbox";
import { EditorThemePanel } from "./panels/EditorThemePanel";
import { EditorSEOPanel } from "./panels/EditorSEOPanel";

interface EditorProps {
  page: PageData;
  onSave: (json: string) => Promise<void>;
  onUpdatePage: (updates: Partial<PageData>) => Promise<void>;
  onBack?: () => void;
  onExport?: (zipBlob: Blob) => void;
  onExportReact?: () => void;
  isExporting?: boolean;
  // For preview navigation
  pages?: PageListItem[];
  onLoadPage?: (slug: string) => Promise<PageData | null>;
  onLoadAllPages?: () => Promise<PageData[]>;
  // Domain-related props for Publish
  hasDomain?: boolean;
  isDomainVerified?: boolean;
  isSitePublished?: boolean;
  isCurrentSitePublished?: boolean; // Is THIS site the one that's published?
  publishedSiteName?: string | null; // Name of the site that's currently published (if different)
  onShowDomainModal?: () => void;
  onPublishSite?: () => void;
  onPublishReplace?: () => void; // Unpublish other site and publish this one
  onUnpublishSite?: () => void;
  isPublishing?: boolean;
  // Site URL and Last Published
  siteUrl?: string | null;
  lastPublished?: string | null;
  // Theme sync callback - notifies parent when theme changes
  onThemeChange?: (theme: ThemeSettings) => void;
  // Site theme as fallback when page.theme is undefined
  siteTheme?: ThemeSettings;
}

// Main Editor Component
export const PageEditorNew = ({
  page,
  onSave,
  onUpdatePage,
  onBack,
  onExport,
  onExportReact,
  pages = [],
  onLoadPage,
  onLoadAllPages,
  hasDomain,
  isDomainVerified,
  isSitePublished,
  isCurrentSitePublished,
  publishedSiteName,
  onShowDomainModal,
  onPublishSite,
  onPublishReplace,
  onUnpublishSite,
  isPublishing,
  siteUrl,
  lastPublished,
  onThemeChange,
  siteTheme,
  isExporting,
}: EditorProps) => {
  const [isPreview, setIsPreview] = useState(false);
  const [activePanel, setActivePanel] = useState<"layers" | "components" | "theme">("layers");
  // Load theme from page data, fall back to site theme, then default theme
  const [theme, setTheme] = useState<ThemeSettings>(page.theme || siteTheme || defaultTheme);
  const [showTheme, setShowTheme] = useState(false);
  const [showSEO, setShowSEO] = useState(false);
  const [previewDevice, setPreviewDevice] = useState<"desktop" | "tablet" | "mobile">("desktop");
  // Panel visibility toggles
  const [showLeftPanel, setShowLeftPanel] = useState(true);
  const [showRightPanel, setShowRightPanel] = useState(true);
  // Preview Modal
  const [showPreviewModal, setShowPreviewModal] = useState(false);

  // MUI theme for canvas area only (blocks use this font, CMS UI stays on Sarabun)
  const canvasTheme = useMemo(
    () =>
      createTheme({
        typography: {
          fontFamily: `'${theme.font}', sans-serif`,
        },
      }),
    [theme.font],
  );

  // Apply theme CSS variables (colors only - font is applied per-canvas via ThemeProvider)
  useEffect(() => {
    document.documentElement.style.setProperty("--color-primary", theme.primary);
    document.documentElement.style.setProperty("--color-secondary", theme.secondary);
  }, [theme]);

  // Load Google Fonts dynamically
  useEffect(() => {
    const fontName = theme.font;
    const fontNameForUrl = fontName.replace(/ /g, "+");
    const linkId = `google-font-${fontNameForUrl}`;

    // Check if font is already loaded
    if (!document.getElementById(linkId)) {
      const link = document.createElement("link");
      link.id = linkId;
      link.rel = "stylesheet";
      link.href = getGoogleFontsUrl(fontName);
      document.head.appendChild(link);
    }
  }, [theme.font]);

  // Keyboard shortcuts
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      // Ctrl+S / Cmd+S to save
      if ((e.ctrlKey || e.metaKey) && e.key === "s") {
        e.preventDefault();
        // Trigger save via custom event (will be handled by EditorTopBar)
        window.dispatchEvent(new CustomEvent("editor-save"));
      }
      // Escape to exit preview
      if (e.key === "Escape" && isPreview) {
        setIsPreview(false);
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [isPreview]);

  // Handle theme change and save to page + sync to site
  const handleThemeChange = async (newTheme: ThemeSettings) => {
    setTheme(newTheme);
    // Auto-save theme to page
    try {
      await onUpdatePage({ theme: newTheme });
      // Notify parent to sync theme to site
      onThemeChange?.(newTheme);
    } catch (error) {
      console.error("Failed to save theme:", error);
    }
  };

  // Handle SEO change and save to page
  const handleSEOChange = async (newSEO: { title?: string; description?: string; keywords?: string }) => {
    try {
      await onUpdatePage({ seo: newSEO });
    } catch (error) {
      console.error("Failed to save SEO:", error);
    }
  };

  // Migrate old page data: force isCanvas for canvas components
  const migratedContent = useMemo(() => {
    if (!page.content) return page.content;
    try {
      const data = typeof page.content === "string" ? JSON.parse(page.content) : page.content;
      const canvasComponents = ["FilterableFeatures"];
      let changed = false;
      for (const [, node] of Object.entries(data)) {
        const n = node as Record<string, unknown>;
        const nodeType = n.type as Record<string, string> | undefined;
        if (nodeType?.resolvedName && canvasComponents.includes(nodeType.resolvedName) && !n.isCanvas) {
          n.isCanvas = true;
          changed = true;
        }
      }
      if (changed) {
        return typeof page.content === "string" ? JSON.stringify(data) : data;
      }
    } catch {
      // If parsing fails, return original
    }
    return page.content;
  }, [page.content]);

  return (
    <CraftEditor key={page.id} resolver={blockResolver} enabled={!isPreview}>
      {/* Keyboard shortcuts handler */}
      <EditorShortcuts />
      {/* Sync node color props when theme changes */}
      <ThemeColorSync theme={theme} />

      <Box sx={{ height: "100%", display: "flex", flexDirection: "column", bgcolor: "grey.100" }}>
        {/* Top Bar */}
        <EditorTopBar
          page={page}
          onSave={onSave}
          onUpdatePage={onUpdatePage}
          isPreview={isPreview}
          setIsPreview={setIsPreview}
          onBack={onBack}
          onShowPreviewModal={() => setShowPreviewModal(true)}
          onExportReact={onExportReact}
          isExporting={isExporting}
          hasDomain={hasDomain}
          isDomainVerified={isDomainVerified}
          isSitePublished={isSitePublished}
          isCurrentSitePublished={isCurrentSitePublished}
          publishedSiteName={publishedSiteName}
          onShowDomainModal={onShowDomainModal}
          onPublishSite={onPublishSite}
          onPublishReplace={onPublishReplace}
          onUnpublishSite={onUnpublishSite}
          isPublishing={isPublishing}
          siteUrl={siteUrl}
          lastPublished={lastPublished}
        />

        {/* Main Content */}
        <Box sx={{ flex: 1, display: "flex", overflow: "hidden" }}>
          {/* Left Panel */}
          {!isPreview && (
            <Paper
              square
              elevation={0}
              sx={{
                bgcolor: "white",
                borderRight: 1,
                borderColor: "divider",
                display: "flex",
                flexDirection: "column",
                position: "relative",
                transition: "width 0.3s",
                width: showLeftPanel ? 256 : 48,
              }}
            >
              {showLeftPanel ? (
                <>
                  {/* Panel Tabs - Enhanced */}
                  <Box
                    sx={{
                      display: "flex",
                      flexWrap: "nowrap",
                      borderBottom: 1,
                      borderColor: "divider",
                      bgcolor: "grey.50",
                    }}
                  >
                    <Tooltip title="View page structure" arrow>
                      <MuiButton
                        onClick={() => setActivePanel("layers")}
                        sx={{
                          flex: 1,
                          minWidth: 0,
                          py: 1,
                          px: 0.5,
                          fontSize: 11,
                          fontWeight: 500,
                          textTransform: "none",
                          borderRadius: 0,
                          borderBottom: activePanel === "layers" ? 2 : 0,
                          borderColor: "primary.main",
                          bgcolor: activePanel === "layers" ? "white" : "transparent",
                          color: activePanel === "layers" ? "primary.main" : "text.secondary",
                        }}
                      >
                        📑
                      </MuiButton>
                    </Tooltip>
                    <Tooltip title="Drag & drop components" arrow>
                      <MuiButton
                        onClick={() => setActivePanel("components")}
                        sx={{
                          flex: 1,
                          minWidth: 0,
                          py: 1,
                          px: 0.5,
                          fontSize: 11,
                          fontWeight: 500,
                          textTransform: "none",
                          borderRadius: 0,
                          borderBottom: activePanel === "components" ? 2 : 0,
                          borderColor: "primary.main",
                          bgcolor: activePanel === "components" ? "white" : "transparent",
                          color: activePanel === "components" ? "primary.main" : "text.secondary",
                        }}
                      >
                        📦
                      </MuiButton>
                    </Tooltip>
                    <Tooltip title="Colors & fonts" arrow>
                      <MuiButton
                        onClick={() => setShowTheme(true)}
                        sx={{
                          flex: 1,
                          minWidth: 0,
                          py: 1,
                          px: 0.5,
                          fontSize: 11,
                          fontWeight: 500,
                          textTransform: "none",
                          borderRadius: 0,
                          color: "text.secondary",
                        }}
                      >
                        🎨
                      </MuiButton>
                    </Tooltip>
                    <Tooltip title="Search optimization" arrow>
                      <MuiButton
                        onClick={() => setShowSEO(true)}
                        sx={{
                          flex: 1,
                          minWidth: 0,
                          py: 1,
                          px: 0.5,
                          fontSize: 11,
                          fontWeight: 500,
                          textTransform: "none",
                          borderRadius: 0,
                          color: "text.secondary",
                        }}
                      >
                        🔍
                      </MuiButton>
                    </Tooltip>
                  </Box>

                  {/* Panel Content */}
                  <Box sx={{ flex: 1, overflow: "hidden", display: "flex", flexDirection: "column" }}>
                    {activePanel === "layers" && <EditorLayersPanel />}
                    {activePanel === "components" && <EditorToolbox />}
                  </Box>

                  {/* Theme Panel Overlay */}
                  {showTheme && (
                    <EditorThemePanel
                      theme={theme}
                      onThemeChange={handleThemeChange}
                      onClose={() => setShowTheme(false)}
                    />
                  )}

                  {/* SEO Panel Overlay */}
                  {showSEO && (
                    <EditorSEOPanel
                      seo={page.seo || {}}
                      pageName={page.name}
                      onSEOChange={handleSEOChange}
                      onClose={() => setShowSEO(false)}
                    />
                  )}
                </>
              ) : (
                /* Collapsed state - vertical icons */
                <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", py: 1, gap: 0.5 }}>
                  <Tooltip title="Layers" placement="right" arrow>
                    <IconButton
                      onClick={() => {
                        setShowLeftPanel(true);
                        setActivePanel("layers");
                      }}
                      sx={{ width: 40, height: 40 }}
                    >
                      <Typography sx={{ fontSize: 18 }}>📑</Typography>
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Add Components" placement="right" arrow>
                    <IconButton
                      onClick={() => {
                        setShowLeftPanel(true);
                        setActivePanel("components");
                      }}
                      sx={{ width: 40, height: 40 }}
                    >
                      <Typography sx={{ fontSize: 18 }}>📦</Typography>
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Theme" placement="right" arrow>
                    <IconButton
                      onClick={() => {
                        setShowLeftPanel(true);
                        setShowTheme(true);
                      }}
                      sx={{ width: 40, height: 40 }}
                    >
                      <Typography sx={{ fontSize: 18 }}>🎨</Typography>
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="SEO" placement="right" arrow>
                    <IconButton
                      onClick={() => {
                        setShowLeftPanel(true);
                        setShowSEO(true);
                      }}
                      sx={{ width: 40, height: 40 }}
                    >
                      <Typography sx={{ fontSize: 18 }}>🔍</Typography>
                    </IconButton>
                  </Tooltip>
                </Box>
              )}

              {/* Toggle button */}
              <IconButton
                onClick={() => setShowLeftPanel(!showLeftPanel)}
                size="small"
                sx={{
                  position: "absolute",
                  right: -12,
                  top: "50%",
                  transform: "translateY(-50%)",
                  width: 24,
                  height: 48,
                  bgcolor: "white",
                  border: 1,
                  borderColor: "grey.200",
                  borderRadius: "0 8px 8px 0",
                  boxShadow: 1,
                  zIndex: 10,
                  "&:hover": { bgcolor: "grey.50" },
                }}
              >
                <ChevronLeftIcon
                  sx={{
                    fontSize: 16,
                    transition: "transform 0.3s",
                    transform: showLeftPanel ? "rotate(0deg)" : "rotate(180deg)",
                  }}
                />
              </IconButton>
            </Paper>
          )}

          {/* Canvas - Enhanced */}
          <Box
            sx={{
              flex: 1,
              overflow: "auto",
              ...(isPreview
                ? { p: 0, display: "flex", flexDirection: "column" }
                : {
                    p: 3,
                    backgroundImage: "radial-gradient(circle at 1px 1px, #e5e7eb 1px, transparent 0)",
                    backgroundSize: "20px 20px",
                  }),
            }}
          >
            {isPreview ? (
              <>
                {/* Preview Toolbar - Enhanced */}
                <Paper
                  square
                  elevation={0}
                  sx={{
                    background: "linear-gradient(to right, #1e293b, #0f172a)",
                    color: "white",
                    py: 1.5,
                    px: 2,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    boxShadow: 3,
                  }}
                >
                  <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      <Box
                        sx={{
                          width: 12,
                          height: 12,
                          borderRadius: "50%",
                          bgcolor: "success.main",
                          animation: "pulse 2s infinite",
                          "@keyframes pulse": {
                            "0%, 100%": { opacity: 1 },
                            "50%": { opacity: 0.5 },
                          },
                        }}
                      />
                      <Typography variant="body2" fontWeight={500}>
                        Live Preview
                      </Typography>
                    </Box>
                    <ToggleButtonGroup
                      value={previewDevice}
                      exclusive
                      onChange={(_, v) => v && setPreviewDevice(v)}
                      size="small"
                      sx={{
                        bgcolor: "rgba(255,255,255,0.1)",
                        "& .MuiToggleButton-root": {
                          border: "none",
                          color: "rgba(255,255,255,0.7)",
                          px: 1.5,
                          py: 0.5,
                          fontSize: 12,
                          textTransform: "none",
                          "&.Mui-selected": {
                            bgcolor: "primary.main",
                            color: "white",
                            boxShadow: 2,
                          },
                        },
                      }}
                    >
                      <ToggleButton value="desktop">
                        <DesktopIcon sx={{ fontSize: 16, mr: 0.5 }} /> Desktop
                      </ToggleButton>
                      <ToggleButton value="tablet">
                        <TabletIcon sx={{ fontSize: 16, mr: 0.5 }} /> Tablet
                      </ToggleButton>
                      <ToggleButton value="mobile">
                        <MobileIcon sx={{ fontSize: 16, mr: 0.5 }} /> Mobile
                      </ToggleButton>
                    </ToggleButtonGroup>
                  </Box>
                  <MuiButton
                    size="small"
                    startIcon={<CloseIcon sx={{ fontSize: 16 }} />}
                    onClick={() => setIsPreview(false)}
                    sx={{
                      color: "white",
                      bgcolor: "rgba(255,255,255,0.1)",
                      textTransform: "none",
                      "&:hover": { bgcolor: "rgba(255,255,255,0.2)" },
                    }}
                  >
                    Exit Preview
                    <Typography
                      component="kbd"
                      sx={{ ml: 1, fontSize: 10, px: 0.75, py: 0.25, bgcolor: "rgba(0,0,0,0.3)", borderRadius: 0.5 }}
                    >
                      Esc
                    </Typography>
                  </MuiButton>
                </Paper>
                {/* Preview Content (SPA mode - inline render instead of iframe) */}
                <Box
                  sx={{
                    flex: 1,
                    background: "linear-gradient(135deg, #475569 0%, #334155 100%)",
                    display: "flex",
                    alignItems: "flex-start",
                    justifyContent: "center",
                    p: 3,
                    overflow: "auto",
                  }}
                >
                  <Box sx={{ position: "relative" }}>
                    {/* Device frame indicator */}
                    {previewDevice !== "desktop" && (
                      <Chip
                        label={previewDevice === "tablet" ? "768px" : "375px"}
                        size="small"
                        sx={{
                          position: "absolute",
                          top: -12,
                          left: "50%",
                          transform: "translateX(-50%)",
                          bgcolor: "grey.600",
                          color: "white",
                          fontSize: 11,
                        }}
                      />
                    )}
                    <ThemeProvider theme={canvasTheme}>
                      <Box
                        sx={{
                          bgcolor: "white",
                          boxShadow: 6,
                          borderRadius: 2,
                          width: previewDevice === "desktop" ? "100%" : previewDevice === "tablet" ? 768 : 375,
                          minHeight: "calc(100vh - 160px)",
                          maxWidth: "100%",
                          overflow: "hidden",
                          fontFamily: `'${theme.font}', sans-serif`,
                          ["--color-primary" as string]: theme.primary,
                          ["--color-secondary" as string]: theme.secondary,
                          ["--font-family" as string]: `'${theme.font}', sans-serif`,
                          ["--font-family-en" as string]: `'${theme.fontEN || theme.font}', sans-serif`,
                        }}
                      >
                        {/* Render content directly in preview mode */}
                        <Frame data={migratedContent}>
                          <Element canvas is={RootContainer}>
                            {/* Content loaded from data */}
                          </Element>
                        </Frame>
                      </Box>
                    </ThemeProvider>
                  </Box>
                </Box>
              </>
            ) : (
              <Box
                sx={{
                  mx: "auto",
                  transition: "max-width 0.3s",
                  maxWidth: !showLeftPanel && !showRightPanel ? 1400 : !showLeftPanel || !showRightPanel ? 1100 : 900,
                }}
              >
                {/* Canvas Header */}
                <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 2 }}>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                    <Box
                      sx={{
                        width: 32,
                        height: 32,
                        borderRadius: 2,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        color: "white",
                        fontSize: 14,
                        fontWeight: 700,
                        boxShadow: 1,
                        background: `linear-gradient(135deg, ${theme.primary} 0%, ${theme.secondary} 100%)`,
                      }}
                    >
                      {page.name.charAt(0).toUpperCase()}
                    </Box>
                    <Box>
                      <Typography variant="body2" fontWeight={600} color="text.primary">
                        {page.name}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        /{page.slug === "home" ? "" : page.slug}
                      </Typography>
                    </Box>
                  </Box>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                    {/* Canvas width indicator */}
                    <Chip
                      size="small"
                      label={
                        !showLeftPanel && !showRightPanel
                          ? "1400px"
                          : !showLeftPanel || !showRightPanel
                            ? "1100px"
                            : "900px"
                      }
                      sx={{ bgcolor: "white", fontSize: 11, boxShadow: 1 }}
                    />
                    {/* Expand hint */}
                    {(showLeftPanel || showRightPanel) && (
                      <Typography variant="caption" color="text.secondary">
                        Collapse panels for wider view
                      </Typography>
                    )}
                  </Box>
                </Box>

                {/* Canvas Content */}
                <ThemeProvider theme={canvasTheme}>
                  <Paper
                    elevation={4}
                    sx={{
                      borderRadius: 3,
                      overflow: "hidden",
                      transition: "all 0.3s",
                      minHeight: "calc(100vh - 200px)",
                      fontFamily: `'${theme.font}', sans-serif`,
                      ["--color-primary" as string]: theme.primary,
                      ["--color-secondary" as string]: theme.secondary,
                      ["--font-family" as string]: `'${theme.font}', sans-serif`,
                      ["--font-family-en" as string]: `'${theme.fontEN || theme.font}', sans-serif`,
                    }}
                  >
                    <Frame data={migratedContent}>
                      <Element canvas is={RootContainer}>
                        {/* Empty */}
                      </Element>
                    </Frame>
                  </Paper>
                </ThemeProvider>

                {/* Canvas Footer */}
                <Box sx={{ mt: 2, textAlign: "center" }}>
                  <Typography variant="caption" color="text.secondary">
                    {showLeftPanel
                      ? "Drag components from the left panel to add content"
                      : "Click the arrow on the left to open components panel"}
                  </Typography>
                </Box>
              </Box>
            )}
          </Box>

          {/* Right Panel - Settings */}
          {!isPreview && (
            <Paper
              square
              elevation={0}
              sx={{
                bgcolor: "white",
                borderLeft: 1,
                borderColor: "divider",
                display: "flex",
                flexDirection: "column",
                position: "relative",
                transition: "width 0.3s",
                width: showRightPanel ? 288 : 48,
                boxShadow: 1,
              }}
            >
              {showRightPanel ? (
                <>
                  <Box
                    sx={{
                      p: 1.5,
                      borderBottom: 1,
                      borderColor: "divider",
                      background: "linear-gradient(to right, #fafafa, #fff)",
                      display: "flex",
                      alignItems: "center",
                      gap: 1,
                    }}
                  >
                    <Box
                      sx={{
                        width: 24,
                        height: 24,
                        bgcolor: "primary.light",
                        borderRadius: 2,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                      }}
                    >
                      <SettingsIcon sx={{ fontSize: 14, color: "primary.main" }} />
                    </Box>
                    <Typography variant="subtitle2" fontWeight={600}>
                      Settings
                    </Typography>
                  </Box>
                  <EditorSettingsPanel />
                </>
              ) : (
                /* Collapsed state */
                <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", py: 1.5 }}>
                  <Tooltip title="Settings" placement="left" arrow>
                    <IconButton onClick={() => setShowRightPanel(true)} sx={{ width: 40, height: 40 }}>
                      <SettingsIcon sx={{ color: "text.secondary" }} />
                    </IconButton>
                  </Tooltip>
                </Box>
              )}

              {/* Toggle button */}
              <IconButton
                onClick={() => setShowRightPanel(!showRightPanel)}
                size="small"
                sx={{
                  position: "absolute",
                  left: -12,
                  top: "50%",
                  transform: "translateY(-50%)",
                  width: 24,
                  height: 48,
                  bgcolor: "white",
                  border: 1,
                  borderColor: "grey.200",
                  borderRadius: "8px 0 0 8px",
                  boxShadow: 1,
                  zIndex: 10,
                  "&:hover": { bgcolor: "grey.50" },
                }}
              >
                <ChevronRightIcon
                  sx={{
                    fontSize: 16,
                    transition: "transform 0.3s",
                    transform: showRightPanel ? "rotate(0deg)" : "rotate(180deg)",
                  }}
                />
              </IconButton>
            </Paper>
          )}
        </Box>
      </Box>

      {/* Preview Modal - uses inline mode for CRA/Vite, iframe for Next.js */}
      {showPreviewModal && (
        <PreviewModal
          pageSlug={page.slug}
          pageName={page.name}
          onClose={() => setShowPreviewModal(false)}
          pageContent={page.content}
          theme={theme}
          pages={pages}
          onLoadPage={onLoadPage}
          onLoadAllPages={onLoadAllPages}
        />
      )}
    </CraftEditor>
  );
};
